import { memo, useState, useCallback, useEffect, useMemo } from 'react';
import {
  Badge,
  Box,
  Button,
  ExpandableSection,
  KeyValuePairs,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import type { StatusIndicatorProps } from '@cloudscape-design/components';
import type { AuditEntry } from '@amzn/helianthus-arbiter-client';
import { AuditEntryType } from '@amzn/helianthus-arbiter-client';
import {
  parseClassificationEntry,
  parseRuleExecutionEntry,
  parseExternalServiceEntry,
  parseErrorDescription,
} from '../utils/auditEntryParsers';
import { ConfidenceBadge } from './ConfidenceBadge';
import { SimpleMarkdown } from '../utils/simpleMarkdown';

interface AuditTrailTimelineProps {
  entries: AuditEntry[];
  evaluationStartTime?: Date;
}

interface EntryTypeVisual {
  icon: StatusIndicatorProps.Type;
  label: string;
}

const ENTRY_TYPE_VISUALS: Record<string, EntryTypeVisual> = {
  [AuditEntryType.EVALUATION_STARTED]: { icon: 'in-progress', label: 'Evaluation Started' },
  [AuditEntryType.INPUT_COLLECTION]: { icon: 'info', label: 'Classification' },
  [AuditEntryType.RULE_EXECUTION]: { icon: 'pending', label: 'Rule Execution' },
  [AuditEntryType.EXTERNAL_SERVICE_CALL]: { icon: 'info', label: 'Model Call' },
  [AuditEntryType.EVALUATION_COMPLETED]: { icon: 'success', label: 'Evaluation Completed' },
  [AuditEntryType.ERROR_OCCURRED]: { icon: 'error', label: 'Error' },
};

function getEntryVisual(entryType: string | undefined): EntryTypeVisual {
  if (!entryType) return { icon: 'info', label: 'Unknown' };
  return ENTRY_TYPE_VISUALS[entryType] ?? { icon: 'info', label: entryType };
}

const reasoningBlockBase: React.CSSProperties = {
  backgroundColor: 'var(--color-background-layout-panel-content)',
  borderRadius: '8px',
  padding: '12px',
  fontSize: 'var(--font-size-body-s)',
  overflowX: 'auto',
};

function formatTimestamp(timestamp: Date | undefined): string {
  if (!timestamp) return '--';
  if (typeof timestamp === 'string') {
    return new Date(timestamp).toLocaleTimeString();
  }
  return timestamp.toLocaleTimeString();
}

function formatRelativeTime(
  timestamp: Date | undefined,
  startTime: Date | undefined,
): string | null {
  if (!timestamp || !startTime) return null;
  const ts = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp.getTime();
  const st = typeof startTime === 'string' ? new Date(startTime).getTime() : startTime.getTime();
  const diffMs = ts - st;
  if (diffMs < 0) return null;
  if (diffMs < 1000) return `+${diffMs}ms`;
  return `+${(diffMs / 1000).toFixed(1)}s`;
}

//Entry detail renderers

const ClassificationDetail = memo(function ClassificationDetail({ description }: { description: string }): React.JSX.Element {
  const parsed = parseClassificationEntry(description);
  if (!parsed) {
    return <Box variant="code" fontSize="body-s">{description}</Box>;
  }
  return (
    <KeyValuePairs
      columns={1}
      items={[
        {
          label: 'Entity Types',
          value: parsed.entityTypesList.length > 0 ? (
            <SpaceBetween direction="horizontal" size="xs">
              {parsed.entityTypesList.map((et) => (
                <Badge key={et} color="blue">{et}</Badge>
              ))}
            </SpaceBetween>
          ) : (
            <Box color="text-status-inactive">None</Box>
          ),
        },
        {
          label: 'Security Domains',
          value: parsed.securityDomainsList.length > 0 ? (
            <SpaceBetween direction="horizontal" size="xs">
              {parsed.securityDomainsList.map((sd) => (
                <Badge key={sd} color="grey">{sd}</Badge>
              ))}
            </SpaceBetween>
          ) : (
            <Box color="text-status-inactive">None</Box>
          ),
        },
        { label: 'Reasoning', value: parsed.reasoning },
      ]}
    />
  );
});

const RuleExecutionDetail = memo(function RuleExecutionDetail({ description }: { description: string }): React.JSX.Element {
  const parsed = parseRuleExecutionEntry(description);
  if (!parsed) {
    return <Box variant="code" fontSize="body-s">{description}</Box>;
  }
  const outcomeType: StatusIndicatorProps.Type =
    parsed.outcome === 'PASS' ? 'success' : parsed.outcome === 'FAIL' ? 'error' : 'warning';
  const items: { label: string; value: React.ReactNode }[] = [
    { label: 'Rule', value: parsed.ruleName },
    {
      label: 'Outcome',
      value: (
        <StatusIndicator type={outcomeType}>
          {parsed.outcome}
        </StatusIndicator>
      ),
    },
  ];
  items.push({ label: 'Confidence', value: <ConfidenceBadge confidence={parsed.confidence} /> });
  if (parsed.severity) {
    items.push({ label: 'Severity', value: parsed.severity });
  }
  if (parsed.evaluatorType) {
    items.push({ label: 'Evaluator Type', value: parsed.evaluatorType });
  }
  if (parsed.durationMs != null) {
    items.push({ label: 'Duration', value: `${parsed.durationMs}ms` });
  }
  if (parsed.error) {
    items.push({ label: 'Error', value: <Box color="text-status-error">{parsed.error}</Box> });
  }
  return (
    <SpaceBetween size="s">
      <KeyValuePairs columns={2} items={items} />
      {parsed.reasoning && (!parsed.evaluatorType || parsed.evaluatorType.toLowerCase() === 'llm') && (
        <ExpandableSection headerText="LLM Reasoning" variant="footer">
          <SpaceBetween size="s">
            <Box>
              <Box variant="h5" margin={{ bottom: 'xxs' }}>Prompt</Box>
              {parsed.reasoning.prompt ? (
                <div style={{
                  ...reasoningBlockBase,
                  border: '1px solid var(--color-border-divider-default)',
                  fontFamily: 'var(--font-family-monospace)',
                  whiteSpace: 'pre-wrap',
                }}>
                  {parsed.reasoning.prompt}
                </div>
              ) : (
                <Box color="text-status-inactive" fontSize="body-s">Not available</Box>
              )}
            </Box>
            <Box>
              <Box variant="h5" margin={{ bottom: 'xxs' }}>Response</Box>
              <div style={{
                ...reasoningBlockBase,
                border: '1px solid var(--color-border-status-info)',
                borderLeft: '4px solid var(--color-border-status-info)',
              }}>
                <SimpleMarkdown content={parsed.reasoning.response} />
              </div>
            </Box>
            {parsed.reasoning.model && (
              <Box color="text-body-secondary" fontSize="body-s">
                Model: {parsed.reasoning.model}
              </Box>
            )}
          </SpaceBetween>
        </ExpandableSection>
      )}
    </SpaceBetween>
  );
});

const ExternalServiceDetail = memo(function ExternalServiceDetail({ description }: { description: string }): React.JSX.Element {
  const parsed = parseExternalServiceEntry(description);
  if (!parsed) {
    return <Box variant="code" fontSize="body-s">{description}</Box>;
  }
  const items: { label: string; value: React.ReactNode }[] = [];
  if (parsed.ruleName) {
    items.push({ label: 'Rule', value: parsed.ruleName });
  }
  items.push({ label: 'Input Tokens', value: String(parsed.inputTokens) });
  items.push({ label: 'Output Tokens', value: String(parsed.outputTokens) });
  if (parsed.modelLatencyMs != null) {
    items.push({ label: 'Model Latency', value: `${parsed.modelLatencyMs}ms` });
  }
  return <KeyValuePairs columns={2} items={items} />;
});

const ErrorDetail = memo(function ErrorDetail({ description }: { description: string }): React.JSX.Element {
  const parsed = parseErrorDescription(description);
  if (!parsed) {
    return (
      <Box variant="code" fontSize="body-s" color="text-status-error">
        {description}
      </Box>
    );
  }
  const items: { label: string; value: React.ReactNode }[] = [];
  if (parsed.errorCode) {
    items.push({ label: 'Error Code', value: <Box variant="code">{parsed.errorCode}</Box> });
  }
  if (parsed.errorCategory) {
    items.push({ label: 'Error Category', value: parsed.errorCategory });
  }
  if (parsed.context) {
    items.push({ label: 'Context', value: parsed.context });
  }
  if (parsed.stackTrace) {
    items.push({
      label: 'Stack Trace',
      value: (
        <ExpandableSection headerText="Stack Trace" variant="footer">
          <Box variant="code" fontSize="body-s">{parsed.stackTrace}</Box>
        </ExpandableSection>
      ),
    });
  }
  if (items.length === 0) {
    return (
      <Box variant="code" fontSize="body-s" color="text-status-error">
        {description}
      </Box>
    );
  }
  return <KeyValuePairs columns={1} items={items} />;
});

function EntryDetail({ entry }: { entry: AuditEntry }): React.JSX.Element | null {
  const description = entry.description;
  if (!description) return null;

  switch (entry.entryType) {
    case AuditEntryType.INPUT_COLLECTION:
      return <ClassificationDetail description={description} />;
    case AuditEntryType.RULE_EXECUTION:
      return <RuleExecutionDetail description={description} />;
    case AuditEntryType.EXTERNAL_SERVICE_CALL:
      return <ExternalServiceDetail description={description} />;
    case AuditEntryType.ERROR_OCCURRED:
      return <ErrorDetail description={description} />;
    default:
      return null;
  }
}

/** Generate a short summary for a collapsed timeline entry header. */
function getEntrySummary(entry: AuditEntry): string | null {
  const description = entry.description;
  if (!description) return null;

  switch (entry.entryType) {
    case AuditEntryType.RULE_EXECUTION: {
      const parsed = parseRuleExecutionEntry(description);
      if (!parsed) return null;
      return parsed.ruleName;
    }
    case AuditEntryType.INPUT_COLLECTION: {
      const parsed = parseClassificationEntry(description);
      if (!parsed) return null;
      const entityCount = parsed.entityTypesList.length;
      const domainCount = parsed.securityDomainsList.length;
      return `${entityCount} entity type${entityCount !== 1 ? 's' : ''}, ${domainCount} domain${domainCount !== 1 ? 's' : ''}`;
    }
    case AuditEntryType.EXTERNAL_SERVICE_CALL: {
      const parsed = parseExternalServiceEntry(description);
      if (!parsed) return null;
      const tokens = parsed.inputTokens + parsed.outputTokens;
      return parsed.ruleName
        ? `${parsed.ruleName} \u2014 ${tokens} tokens`
        : `${tokens} tokens`;
    }
    case AuditEntryType.ERROR_OCCURRED: {
      const parsed = parseErrorDescription(description);
      if (!parsed) return null;
      return parsed.errorCode ?? parsed.errorCategory ?? 'Error';
    }
    default:
      return null;
  }
}

/** Get the outcome for a rule execution entry (for collapsed badge display). */
function getRuleOutcome(entry: AuditEntry): { outcome: string; type: StatusIndicatorProps.Type } | null {
  if (entry.entryType !== AuditEntryType.RULE_EXECUTION || !entry.description) return null;
  const parsed = parseRuleExecutionEntry(entry.description);
  if (!parsed) return null;
  const type: StatusIndicatorProps.Type =
    parsed.outcome === 'PASS' ? 'success' : parsed.outcome === 'FAIL' ? 'error' : 'warning';
  return { outcome: parsed.outcome, type };
}

//Bookend entries (started / completed) -- no expandable section

function isBookendEntry(entry: AuditEntry): boolean {
  return (
    entry.entryType === AuditEntryType.EVALUATION_STARTED ||
    entry.entryType === AuditEntryType.EVALUATION_COMPLETED
  );
}

//Timeline node styles (CSS-in-JS via inline)

const timelineLineStyle: React.CSSProperties = {
  position: 'absolute',
  left: '11px',
  top: '24px',
  bottom: '0',
  width: '2px',
  backgroundColor: 'var(--color-border-divider-default)',
};

const timelineDotStyle: React.CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'var(--color-text-status-info)',
  marginTop: '6px',
  flexShrink: 0,
};

const timelineNodeStyle: React.CSSProperties = {
  display: 'flex',
  gap: '16px',
  position: 'relative',
  paddingBottom: '20px',
};

//Main component

export function AuditTrailTimeline({ entries, evaluationStartTime }: AuditTrailTimelineProps): React.JSX.Element {
  if (entries.length === 0) {
    return (
      <Box textAlign="center" color="text-body-secondary" padding={{ top: 'l' }}>
        No audit trail entries found.
      </Box>
    );
  }

  // Use the first entry timestamp as start reference if not provided
  const startTime = evaluationStartTime ?? entries[0]?.timestamp;

  // Track which entries are expanded by index. Default: ERROR_OCCURRED entries start expanded.
  const defaultExpanded = useMemo(() => {
    const set = new Set<number>();
    entries.forEach((entry, i) => {
      if (entry.entryType === AuditEntryType.ERROR_OCCURRED) {
        set.add(i);
      }
    });
    return set;
  }, [entries]);

  const [expandedSet, setExpandedSet] = useState<Set<number>>(defaultExpanded);

  // Reset expanded state when entries change (e.g., re-fetch)
  useEffect(() => {
    setExpandedSet(defaultExpanded);
  }, [defaultExpanded]);

  const allExpandableIndices = useMemo(() => {
    const indices: number[] = [];
    entries.forEach((entry, i) => {
      if (!isBookendEntry(entry) && entry.description) {
        indices.push(i);
      }
    });
    return indices;
  }, [entries]);

  const allExpanded = useMemo(
    () => allExpandableIndices.length > 0 && allExpandableIndices.every(i => expandedSet.has(i)),
    [allExpandableIndices, expandedSet],
  );

  const handleToggleAll = useCallback(() => {
    if (allExpanded) {
      setExpandedSet(new Set<number>());
    } else {
      setExpandedSet(new Set<number>(allExpandableIndices));
    }
  }, [allExpanded, allExpandableIndices]);

  const handleToggleEntry = useCallback((index: number, expanded: boolean) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (expanded) {
        next.add(index);
      } else {
        next.delete(index);
      }
      return next;
    });
  }, []);

  return (
    <SpaceBetween size="s">
      {allExpandableIndices.length > 0 && (
        <Box float="right">
          <Button
            variant="normal"
            iconName={allExpanded ? 'treeview-collapse' : 'treeview-expand'}
            onClick={handleToggleAll}
          >
            {allExpanded ? 'Collapse All' : 'Expand All'}
          </Button>
        </Box>
      )}
      <div style={{ position: 'relative' }}>
      {entries.map((entry, index) => {
        const visual = getEntryVisual(entry.entryType);
        const isLast = index === entries.length - 1;
        const isBookend = isBookendEntry(entry);
        const relative = formatRelativeTime(entry.timestamp, startTime);
        const hasDetail = !isBookend && entry.description;
        const summary = hasDetail ? getEntrySummary(entry) : null;
        const ruleOutcome = getRuleOutcome(entry);
        const headerContent = summary
          ? `Details \u2014 ${summary}`
          : 'Details';

        return (
          <div key={index} style={timelineNodeStyle}>
            {/* Vertical connector line */}
            {!isLast && <div style={timelineLineStyle} />}

            {/* Dot */}
            <div style={timelineDotStyle} />

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <SpaceBetween size="xxs">
                {/* Summary line */}
                <Box>
                  <SpaceBetween direction="horizontal" size="xs">
                    <StatusIndicator type={visual.icon}>
                      {visual.label}
                    </StatusIndicator>
                    <Box color="text-body-secondary" fontSize="body-s">
                      {formatTimestamp(entry.timestamp)}
                      {relative && ` (${relative})`}
                    </Box>
                  </SpaceBetween>
                </Box>

                {/* Expandable detail (only for non-bookend entries with description) */}
                {hasDetail && (
                  <SpaceBetween direction="horizontal" size="xs" alignItems="center">
                    <ExpandableSection
                      headerText={headerContent}
                      variant="footer"
                      expanded={expandedSet.has(index)}
                      onChange={({ detail }) => handleToggleEntry(index, detail.expanded)}
                    >
                      <EntryDetail entry={entry} />
                    </ExpandableSection>
                    {ruleOutcome && (
                      <StatusIndicator type={ruleOutcome.type}>
                        {ruleOutcome.outcome}
                      </StatusIndicator>
                    )}
                  </SpaceBetween>
                )}
              </SpaceBetween>
            </div>
          </div>
        );
      })}
      </div>
    </SpaceBetween>
  );
}
